/* eslint-disable react/display-name */
import {DeleteTwoTone, DownloadOutlined, ExclamationCircleTwoTone,} from '@ant-design/icons';
import {Button, Popconfirm, Table, Tooltip} from 'antd';
import React, {Fragment, useEffect, useState} from 'react';
import {isEmpty} from 'underscore';
import Api from '../../../api/Api';
import {CartTableItem} from '../../../types/home';
import styles from './ShoppingCart.module.scss';
import CartDetails from './CartDetails';
import {useSelectedDataset} from "../../layout/Header/components/SearchBar";

export default function CartTable(props: {
  tableData: CartTableItem[];
  delete: (item: CartTableItem) => {};
  loading: boolean;
}) {
  const ERRORCOLUMN = 'Status';
  const { onChange }= useSelectedDataset()

  const { tableData, loading } = props;

  const handleDelete = (item: CartTableItem) => {
    props.delete(item);
  };

  const [columns, setColumns] = useState([
    {
      title: 'Point Name',
      dataIndex: 'pointName',
      key: 'pointName',
      className: 'PointNameColumn',
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: 'Dataset',
      dataIndex: 'acronym',
      key: 'acronym',
      className: 'DatasetColumn',
      render: (_text: any, record: CartTableItem, _index: any) => (
        <Tooltip title={'Click to see metadata'}>
          <span id={`Cart-Dataset_Link_${record.datasetId}`} onClick={() => onPointNameClick(record)}>
            {record.acronym ? record.acronym : record.Dataset}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Sample data (.csv)',
      key: 'sample',
      render: (_text: any, record: CartTableItem, _index: any) => (
        <Button
          id={`Cart-Dataset_Download_Button_${record.datasetId}`}
          type="primary"
          icon={<DownloadOutlined />}
          size={'small'}
          onClick={async () => window.open(await getSampleDataLink(record))}
        >
          Download
        </Button>
      ),
    },
    {
      title: 'Location',
      dataIndex: 'Location',
      key: 'Location',
    },
    {
      title: 'Time',
      dataIndex: 'TimeStr',
      key: 'TimeStr',
    },
    {
      title: 'Price',
      dataIndex: 'Price',
      key: 'Price',
      render: (_text: any, record: CartTableItem, _index: number) =>
        `€ ${record.Price.toFixed(2)}`,
    },
    {
      title: 'Operation',
      dataIndex: 'Operation',
      key: 'Operation',
      width: '125px',
      render: (_text: any, record: CartTableItem, _index: any) => (
        <Popconfirm
          placement="left"
          title="Are you sure you want to remove this item?"
          onConfirm={() => handleDelete(record)}
          okButtonProps={{ id: `Cart-Dataset_Remove_Confirm_Yes_${record.datasetId}` }}
          cancelButtonProps={{ id: `Cart-Dataset_Remove_Confirm_No_${record.datasetId}` }}
          okText="Yes"
          cancelText="No"
        >
          <Button
            id={`Cart-Dataset_Remove_Button_${record.datasetId}`}
            key={Math.random()}
            type="link"
            icon={<DeleteTwoTone style={{ color: '#666666' }} />}
            style={{ padding: 0 }}
          >
            REMOVE
          </Button>
        </Popconfirm>
      ),
    },
  ]);

  const errorColumn = {
    title: ERRORCOLUMN,
    dataIndex: 'hasError',
    key: 'hasError',
    className: '',
    width: undefined,
    render: (_text: any, record: CartTableItem, _index: any) => (
      <Fragment>
        {record.hasError ? (
          <Button
            key={Math.random()}
            type="link"
            icon={<ExclamationCircleTwoTone style={{ color: '#666666' }} />}
            style={{ padding: 0 }}
            loading={record.isPending}
          >
            {record.isPending ? 'Pending' : 'Failed'}
          </Button>
        ) : null}
      </Fragment>
    ),
  };

  useEffect(() => {
    if (tableData && tableData.length > 0) {
      const errors = tableData.find((item: CartTableItem) => item.hasError);
      const removeError = columns.filter(
        (column) => column.title !== ERRORCOLUMN
      );
      if (errors) {
        setColumns([...removeError, errorColumn]);
      } else {
        setColumns(removeError);
      }
    }
  }, [tableData]);

  function onPointNameClick(record: CartTableItem) {
    if (isEmpty(record.datasetId)) {
      return;
    }
    onChange(record.datasetId!);
  }

  const getSampleDataLink = async (record: CartTableItem) => {
    if (record.datasetId) {
      const meta = await Api.detail.searchDatasetDetail(record.datasetId);
      return meta?.['Variables_Sample Data (.csv)']?.link ?? '';
    }
  };

  return (
    <Table
      size="small"
      bordered
      dataSource={tableData}
      columns={columns}
      className={styles.cartTable}
      pagination={{ defaultPageSize: 5 }}
      expandedRowRender={(record: CartTableItem) => (
        <CartDetails data={record.Detail}></CartDetails>
      )}
      expandedRowKeys={tableData.filter((d) => d.showDetail).map((d) => d.key)}
      expandIconColumnIndex={-1}
      expandIcon={() => null}
      loading={loading}
    />
  );
}
